import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Dd } from './Dropdown.js';
import { DateField } from './DateTimeField.js';
import { ordinalOfMonth, nextOccurrence, recurrenceEnded } from '@ehr/shared';

// ehr#488: the date math moved to @ehr/shared (the server rolls a repeating task forward too).
export { ordinalOfMonth, nextOccurrence, recurrenceEnded };

// Google-Calendar-style recurrence (#4, #22), ported 1:1 from the EHR_08-14-V38
// mockup (recurrencePresets / recurrenceFromPreset / the Custom recurrence dialog).
// The field is a date-contextual preset dropdown ("Weekly on Thu", "Monthly on the
// second Tue", …) whose "Custom…" option opens a dialog with interval, weekday
// chips, monthly mode, and an Ends condition. A null rule means "Does not repeat".

export type Freq = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface RecurrenceRule {
  freq: Freq;
  /** Every N units (>= 1). */
  interval: number;
  /** Weekly only: 0=Sun … 6=Sat. Empty = same weekday as the anchor date. */
  weekdays: number[];
  /** Monthly only: "on day 15" vs "on the 3rd Wed". */
  monthlyMode: 'day' | 'weekday';
  ends: { type: 'never' | 'on' | 'after'; date: string | null; count: number | null };
  /** How many occurrences have completed (drives the "after N" end condition). */
  countDone: number;
}

/** A recurrence value: a rule, or null for "Does not repeat". */
export type Recurrence = RecurrenceRule | null;

export const NO_RECURRENCE: Recurrence = null;

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const ORDINALS = ['first', 'second', 'third', 'fourth', 'last'];

// ── date helpers (date-only, local) ──────────────────────────────────────────
function parseISO(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

interface Preset {
  id: string;
  label: string;
}

/** Preset list for the Repeat dropdown, contextual to the anchor date. */
export function recurrencePresets(dueISO: string): Preset[] {
  const d = parseISO(dueISO) ?? new Date();
  const wd = d.getDay();
  const { nth, isLast } = ordinalOfMonth(d);
  const ordWord = isLast ? 'last' : ORDINALS[nth];
  return [
    { id: 'none', label: 'Does not repeat' },
    { id: 'daily', label: 'Daily' },
    { id: 'weekly', label: `Weekly on ${WEEKDAY_LABELS[wd]}` },
    { id: 'monthly', label: `Monthly on the ${ordWord} ${WEEKDAY_LABELS[wd]}` },
    {
      id: 'yearly',
      label: `Annually on ${d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`,
    },
    { id: 'weekday', label: 'Every weekday (Mon–Fri)' },
    { id: 'custom', label: 'Custom…' },
  ];
}

const baseRule = (): Omit<RecurrenceRule, 'freq'> => ({
  interval: 1,
  weekdays: [],
  monthlyMode: 'weekday',
  ends: { type: 'never', date: null, count: null },
  countDone: 0,
});

/** Turn a preset id + anchor date into a recurrence rule (null for 'none'). */
export function recurrenceFromPreset(id: string, dueISO: string): Recurrence {
  const d = parseISO(dueISO) ?? new Date();
  const wd = d.getDay();
  switch (id) {
    case 'daily':
      return { ...baseRule(), freq: 'daily' };
    case 'weekly':
      return { ...baseRule(), freq: 'weekly', weekdays: [wd] };
    case 'monthly':
      return { ...baseRule(), freq: 'monthly', monthlyMode: 'weekday' };
    case 'yearly':
      return { ...baseRule(), freq: 'yearly' };
    case 'weekday':
      return { ...baseRule(), freq: 'weekly', weekdays: [1, 2, 3, 4, 5] };
    case 'custom':
      return { ...baseRule(), freq: 'weekly', weekdays: [wd] }; // seed; the dialog refines
    default:
      return null;
  }
}

/** Which preset id currently matches a rule (for the dropdown selection). */
export function matchRecurrencePreset(rec: Recurrence, dueISO: string): string {
  if (!rec) return 'none';
  const d = parseISO(dueISO) ?? new Date();
  const wd = d.getDay();
  const isCustom = rec.interval > 1 || (rec.ends && rec.ends.type !== 'never');
  if (isCustom) return 'custom';
  if (rec.freq === 'daily') return 'daily';
  if (rec.freq === 'weekly') {
    const w = rec.weekdays ?? [];
    if (w.length === 5 && [1, 2, 3, 4, 5].every((x) => w.includes(x))) return 'weekday';
    if (w.length <= 1 && (w.length === 0 || w[0] === wd)) return 'weekly';
    return 'custom';
  }
  if (rec.freq === 'monthly') return rec.monthlyMode === 'weekday' ? 'monthly' : 'custom';
  if (rec.freq === 'yearly') return 'yearly';
  return 'custom';
}

/** Human summary, e.g. "Every 2 weeks on Mon, Wed, 5 times". */
export function recurLabel(rec: Recurrence): string {
  if (!rec || !rec.freq) return 'Does not repeat';
  const n = rec.interval || 1;
  const unit = { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' }[rec.freq];
  let s =
    n === 1
      ? { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Annually' }[rec.freq]
      : `Every ${n} ${unit}s`;
  if (rec.freq === 'weekly' && (rec.weekdays ?? []).length) {
    const w = [...rec.weekdays].sort((a, b) => a - b);
    if (w.length === 5 && [1, 2, 3, 4, 5].every((x) => w.includes(x)))
      s = n === 1 ? 'Every weekday' : `${s} on weekdays`;
    else s += ' on ' + w.map((i) => WEEKDAY_LABELS[i]).join(', ');
  }
  if (rec.ends) {
    if (rec.ends.type === 'on' && rec.ends.date) {
      const e = parseISO(rec.ends.date);
      if (e)
        s += `, until ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (rec.ends.type === 'after' && rec.ends.count) {
      s += `, ${rec.ends.count} times`;
    }
  }
  return s;
}

/** ehr#488: the line under the repeat pills: when the next one is due, or that this is the last. */
export const repeatHint = (
  dueISO: string,
  rec: RecurrenceRule,
  thisYear = new Date().getFullYear(),
): string => {
  const next = nextOccurrence(dueISO, rec);
  if (recurrenceEnded(rec, next)) return 'Last one in the series: completing it ends the repeat.';
  const [y, m, d] = next.split('-').map(Number);
  const when = new Date(y!, m! - 1, d!).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(y !== thisYear ? { year: 'numeric' } : {}),
  });
  return `When it's completed, the next one is made, due ${when}.`;
};

function RepeatIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="23 4 23 10 17 10" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
    </svg>
  );
}

// ── Custom recurrence dialog ─────────────────────────────────────────────────
function CustomRecurrenceModal({
  anchorISO,
  current,
  onSave,
  onClose,
}: {
  anchorISO: string;
  current: Recurrence;
  onSave: (rec: RecurrenceRule) => void;
  onClose: () => void;
}) {
  const anchor = parseISO(anchorISO) ?? new Date();
  const [draft, setDraft] = useState<RecurrenceRule>(() =>
    current
      ? { ...current, ends: current.ends ?? { type: 'never', date: null, count: null } }
      : {
          freq: 'weekly',
          interval: 1,
          weekdays: [anchor.getDay()],
          monthlyMode: 'weekday',
          ends: { type: 'never', date: null, count: null },
          countDone: 0,
        },
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { nth, isLast } = ordinalOfMonth(anchor);
  const ordWord = isLast ? 'last' : ORDINALS[nth];
  const set = (patch: Partial<RecurrenceRule>) => setDraft((d) => ({ ...d, ...patch }));
  const setEnds = (patch: Partial<RecurrenceRule['ends']>) =>
    setDraft((d) => ({ ...d, ends: { ...d.ends, ...patch } }));
  const toggleWd = (wd: number) =>
    setDraft((d) => {
      const w = new Set(d.weekdays);
      if (w.has(wd)) w.delete(wd);
      else w.add(wd);
      return { ...d, weekdays: [...w] };
    });

  const done = () => {
    let out = draft;
    if (out.freq === 'weekly' && out.weekdays.length === 0)
      out = { ...out, weekdays: [anchor.getDay()] };
    onSave(out);
  };

  const plural = draft.interval > 1;
  const unitOpts = [
    { value: 'daily', label: `day${plural ? 's' : ''}` },
    { value: 'weekly', label: `week${plural ? 's' : ''}` },
    { value: 'monthly', label: `month${plural ? 's' : ''}` },
    { value: 'yearly', label: `year${plural ? 's' : ''}` },
  ];

  // Portal to <body>: the dialog is a fixed-position modal, and rendering it in
  // place would nest it inside the Follow-up tab's `.field`, whose global
  // `.field input:not([type=checkbox]){width:100%;appearance:none}` rule would
  // stretch the inputs and strip the radios into boxes. At <body> it escapes that.
  return createPortal(
    <div className="recur-overlay" onClick={onClose}>
      <div
        className="recur-dialog"
        role="dialog"
        aria-label="Custom recurrence"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="recur-dialog-head">
          <h3>Custom recurrence</h3>
          <button type="button" className="recur-x" onClick={onClose} aria-label="Close">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="recur-dialog-body">
          <div className="recur-row">
            <span className="recur-row-label">Repeat every</span>
            <input
              type="number"
              min={1}
              className="recur-interval"
              value={draft.interval}
              onChange={(e) => set({ interval: Math.max(1, parseInt(e.target.value, 10) || 1) })}
              aria-label="Repeat interval"
            />
            <Dd
              value={draft.freq}
              searchable={false}
              options={unitOpts}
              onChange={(v) => set({ freq: v as Freq })}
            />
          </div>

          {draft.freq === 'weekly' && (
            <div className="recur-block">
              <span className="recur-row-label">Repeat on</span>
              <div className="recur-weekdays">
                {WEEKDAY_SHORT.map((lbl, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`recur-wd${draft.weekdays.includes(i) ? ' on' : ''}`}
                    aria-pressed={draft.weekdays.includes(i)}
                    aria-label={WEEKDAY_LABELS[i]}
                    title={WEEKDAY_LABELS[i]}
                    onClick={() => toggleWd(i)}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          )}

          {draft.freq === 'monthly' && (
            <div className="recur-block">
              <Dd
                value={draft.monthlyMode}
                searchable={false}
                options={[
                  { value: 'day', label: `Monthly on day ${anchor.getDate()}` },
                  {
                    value: 'weekday',
                    label: `Monthly on the ${ordWord} ${WEEKDAY_LABELS[anchor.getDay()]}`,
                  },
                ]}
                onChange={(v) => set({ monthlyMode: v as 'day' | 'weekday' })}
              />
            </div>
          )}

          <div className="recur-block">
            <span className="recur-row-label">Ends</span>
            <label className="recur-ends-opt">
              <input
                type="radio"
                name="recur-ends"
                checked={draft.ends.type === 'never'}
                onChange={() => setEnds({ type: 'never' })}
              />
              Never
            </label>
            <label className="recur-ends-opt">
              <input
                type="radio"
                name="recur-ends"
                checked={draft.ends.type === 'on'}
                onChange={() => setEnds({ type: 'on' })}
              />
              On
              <DateField
                value={draft.ends.date ?? ''}
                disabled={draft.ends.type !== 'on'}
                onChange={(v) => setEnds({ date: v || null })}
              />
            </label>
            <label className="recur-ends-opt">
              <input
                type="radio"
                name="recur-ends"
                checked={draft.ends.type === 'after'}
                onChange={() => setEnds({ type: 'after' })}
              />
              After
              <input
                type="number"
                min={1}
                style={{ width: 56 }}
                value={draft.ends.count ?? 13}
                disabled={draft.ends.type !== 'after'}
                onChange={(e) => setEnds({ count: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                aria-label="Ends after N occurrences"
              />
              occurrences
            </label>
          </div>

          <div className="recur-summary">{recurLabel(draft)}</div>
        </div>

        <div className="recur-dialog-foot">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" onClick={done}>
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function RecurrenceField({
  rule,
  onChange,
  anchorISO,
  disabled,
  variant = 'dropdown',
}: {
  rule: Recurrence;
  onChange: (r: Recurrence) => void;
  anchorISO: string;
  disabled?: boolean;
  /** 'dropdown' (Follow-up) or 'pills' (Tasks, matching the EHR_08-14-V38 mockup). */
  variant?: 'dropdown' | 'pills';
}) {
  const [customOpen, setCustomOpen] = useState(false);
  const presets = recurrencePresets(anchorISO);
  const value = matchRecurrencePreset(rule, anchorISO);

  const onSelect = (id: string) => {
    if (id === 'custom') {
      setCustomOpen(true);
      return;
    }
    onChange(recurrenceFromPreset(id, anchorISO));
  };

  const customModal = customOpen ? (
    <CustomRecurrenceModal
      anchorISO={anchorISO}
      current={rule}
      onClose={() => setCustomOpen(false)}
      onSave={(rec) => {
        onChange(rec);
        setCustomOpen(false);
      }}
    />
  ) : null;

  // Pills variant (Tasks): every preset is a pill, matching the mockup. ehr#488: every pill works,
  // Custom… too; they were disabled (looking no different) until the task had a due date. The
  // Tasks drawer passes today as the anchor then, and makes today the due date on a pick.
  if (variant === 'pills') {
    return (
      <div className={disabled ? 'rec rec-disabled' : 'rec'}>
        <div className="note-type-pills">
          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`note-type-pill${value === p.id ? ' active' : ''}`}
              aria-pressed={value === p.id}
              disabled={disabled}
              onClick={() => onSelect(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
        {rule && value === 'custom' && (
          <button
            type="button"
            className="rec-edit-line"
            onClick={() => !disabled && setCustomOpen(true)}
            title="Edit custom recurrence"
          >
            <RepeatIcon /> {recurLabel(rule)}
          </button>
        )}
        {customModal}
      </div>
    );
  }

  return (
    <div className={disabled ? 'rec rec-disabled' : 'rec'}>
      <Dd
        value={value}
        searchable={false}
        options={presets.map((p) => ({ value: p.id, label: p.label }))}
        onChange={onSelect}
        placeholder="Does not repeat"
      />
      {rule && (
        <button
          type="button"
          className="rec-edit-line"
          onClick={() => !disabled && setCustomOpen(true)}
          title="Edit custom recurrence"
        >
          <RepeatIcon /> {recurLabel(rule)}
        </button>
      )}
      {customModal}
    </div>
  );
}
